import { Image } from 'expo-image'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React from 'react'
import { Text, View } from 'react-native'
import { P, match } from 'ts-pattern'

import { stepContent } from '@/components/flowStep/styles'
import type { UseZodForm } from '@/shared/components/flows/ZodForm'
import { ZodSubmitButton } from '@/shared/components/flows/ZodSubmitButton'
import { getPathWithParams } from '@/utils/url'
import type { processStepContentElementText } from '@flows-ui/utils/processStepContentElementText'
import chevronLeft from '@ui-kit/assets/images/src/icons/small/action/chevronLeft_24.svg'
import Button from '@ui-kit/components/Button'

import type { FlowStepData } from './types'
import type { useFlowSummary } from './useFlowSummary'

const FooterPrice = ({
  data,
  summary,
  newDesign,
}: {
  data: FlowStepData
  summary: ReturnType<typeof useFlowSummary>
  newDesign?: boolean
}) => {
  const { t } = useTranslation()
  const { isPaymentStep, isQuoteStep } = data.stepFlags
  if (!newDesign || !data.priceData || isPaymentStep || isQuoteStep) {
    return null
  }
  const { stickyPrice, stickyPriceValue, stickyPriceInterval } = stepContent()
  return (
    <View className={stickyPrice()} testID="sticky_price">
      <Text className={stickyPriceValue()}>{summary.formattedFinalPremium}</Text>
      <Text className={stickyPriceInterval()}>
        {t(`sharedFlows_common:steps.footer.perInterval.${summary.priceInterval}`)}
      </Text>
    </View>
  )
}

const getBuyLabelKey = (prefix: string, summary: ReturnType<typeof useFlowSummary>) =>
  summary.appliedDiscount.type === 'coupon'
    ? `${prefix}.coupon_discount.button_label.${summary.priceInterval}`
    : `${prefix}.button_label.${summary.priceInterval}`

const FooterBackButton = ({
  flowName,
  previousStep,
  whitelistedParams,
  onBack,
  newDesign,
}: {
  flowName: string
  previousStep: FlowStepData['previousStep']
  whitelistedParams: string
  onBack: () => void
  newDesign?: boolean
}) => {
  const { backButton, backButtonImageContainer, backButtonImage } = stepContent({ newDesign })
  if (!previousStep) {
    return null
  }
  return (
    <Link
      replace
      href={getPathWithParams(`/flows/${flowName}/${previousStep}`, { params: whitelistedParams })}
      data-testid="step_back_button"
      className={backButton()}
      onClick={onBack}
    >
      <View className={backButtonImageContainer()}>
        <Image source={chevronLeft.src} className={backButtonImage()} alt="Step back" />
      </View>
    </Link>
  )
}

const getSubmitLabel = (
  t: (key: string) => string,
  {
    isPaymentStep,
    purchasing,
    customLabel,
    buyLabel,
  }: {
    isPaymentStep: boolean
    purchasing: boolean
    customLabel?: string | false
    buyLabel: string
  }
) => {
  if (!isPaymentStep) {
    return customLabel || t('common:flows.cta.continue')
  }
  return purchasing ? t('sharedFlows_common:steps.payment.loading') : buyLabel
}

export const FlowStepFooter = ({
  alignWithContent,
  newDesign,
  arePaymentDetailsValid,
  buyButtonLabelPrefix = 'sharedFlows_common:steps.confirmations',
  consentMissing,
  data,
  flowName,
  form,
  isSubmitting,
  isPriceRequestPending,
  onBack,
  purchasing,
  summary,
  textProcessor,
  whitelistedParams,
}: {
  alignWithContent?: boolean
  newDesign?: boolean
  arePaymentDetailsValid: boolean
  buyButtonLabelPrefix?: string
  consentMissing: boolean
  data: FlowStepData
  flowName: string
  form: UseZodForm<Record<string, unknown>>
  isSubmitting: boolean
  isPriceRequestPending: boolean
  onBack: () => void
  purchasing: boolean
  summary: ReturnType<typeof useFlowSummary>
  textProcessor: ReturnType<typeof processStepContentElementText>
  whitelistedParams: string
}) => {
  const { t } = useTranslation()
  const { push } = useRouter()
  const { footerContent, continueButtonLabelWrapper } = stepContent({
    alignFooterWithContent: alignWithContent,
    newDesign,
  })

  const actionButton = match(data)
    .with({ hasNext: true, hideFooter: false }, () => {
      const { isPaymentStep } = data.stepFlags

      return (
        <ZodSubmitButton
          form={form}
          loading={isSubmitting || purchasing}
          type={'default'}
          label={getSubmitLabel(t, {
            isPaymentStep,
            purchasing,
            customLabel: data.stepButton?.buttonLabel && textProcessor(data.stepButton.buttonLabel),
            buyLabel: t(getBuyLabelKey(buyButtonLabelPrefix, summary), {
              price: summary.formattedFinalPremium,
            }),
          })}
          disabled={
            isPriceRequestPending || (isPaymentStep && (!arePaymentDetailsValid || consentMissing))
          }
        />
      )
    })
    .with({ stepButton: P.not(P.nullish), hideFooter: false }, ({ stepButton }) => (
      <Button
        id="proceed_button"
        type={stepButton.buttonType ?? 'default'}
        variantColor="black"
        onPress={() => push('/')}
        label={textProcessor(stepButton.buttonLabel)}
      />
    ))
    .otherwise(() => null)

  return (
    <View className={footerContent()}>
      <FooterPrice data={data} summary={summary} newDesign={newDesign} />
      <FooterBackButton
        flowName={flowName}
        previousStep={data.previousStep}
        whitelistedParams={whitelistedParams}
        onBack={onBack}
        newDesign={newDesign}
      />
      {actionButton ? <View className={continueButtonLabelWrapper()}>{actionButton}</View> : null}
    </View>
  )
}
